// @ts-nocheck
import { splitCamelCase } from "@insite/client-framework/Common/StringHelpers";
import { CartModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import FieldDefinition, { Option } from "@insite/client-framework/Types/FieldDefinition";
import { getContextualCssUnitField } from "@store/Framework/Common/Utilities/FieldHelpers";
import * as cssLinter from "css";

export enum fields {
    selectedProperty = "selectedProperty",
    customLabel = "customLabel",
    titleType = "titleType",
    titleStyle = "titleStyle",
    layout = "layout",
    gap = "gap",
    customProperty = "customProperty",
    customCSS = "customCSS",
    customPath = "customPath",
    customPathType = "customPathType",
    customPathOverride = "customPathOverride",
    defaultValue = "defaultValue",
}
const getAllDistinctPropertiesAsOptions = (): Option<string>[] => {
    return Object.keys(allProperties)
        .map(key => ({
            displayName: splitCamelCase(key),
            value: key,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

const gapField = getContextualCssUnitField(fields.gap);
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `
& {
}

.wrapper {
}

.title {
}

.property {
}
`;
const cartProperties: { [key in keyof Partial<CartModel>]: "boolean" | "string" | "number" | "date" | "custom" } = {
    additionalEmails: "string",
    approverReason: "string",
    canBypassCheckoutAddress: "boolean",
    canCheckOut: "boolean",
    canEditCostCode: "boolean",
    canModifyOrder: "boolean",
    canRequestQuote: "boolean",
    canRequisition: "boolean",
    canSaveOrder: "boolean",
    cartLinesUri: "string",
    cartNotPriced: "boolean",
    costCodeLabel: "string",
    currencySymbol: "string",
    customerVatNumber: "string",
    displayContinueShoppingLink: "boolean",
    erpOrderNumber: "string",
    failedToGetRealTimeInventory: "boolean",
    fulfillmentMethod: "string",
    hasApprover: "boolean",
    hasInsufficientInventory: "boolean",
    id: "string",
    initiatedByUserName: "string",
    isAuthenticated: "boolean",
    isAwaitingApproval: "boolean",
    isGuestOrder: "boolean",
    isSalesperson: "boolean",
    isSubscribed: "boolean",
    lineCount: "number",
    notes: "string",
    orderDate: "date",
    orderGrandTotal: "number",
    orderGrandTotalDisplay: "string",
    orderNumber: "string",
    orderSubTotal: "number",
    orderSubTotalDisplay: "string",
    orderSubTotalWithOutProductDiscounts: "number",
    orderSubTotalWithOutProductDiscountsDisplay: "string",
    poNumber: "string",
    promotionCode: "string",
    properties: "custom",
    quoteRequiredCount: "number",
    requestedDeliveryDate: "string",
    requestedDeliveryDateDisplay: "date",
    requestedPickupDate: "string",
    requestedPickupDateDisplay: "date",
    requiresApproval: "boolean",
    requiresPoNumber: "boolean",
    salespersonName: "string",
    shippingAndHandling: "number",
    shippingAndHandlingDisplay: "string",
    shipToLabel: "string",
    showCostCode: "boolean",
    showCreditCard: "boolean",
    showECheck: "boolean",
    showLineNotes: "boolean",
    showNewsletterSignup: "boolean",
    showPayPal: "boolean",
    showPoNumber: "boolean",
    showTaxAndShipping: "boolean",
    status: "string",
    statusDisplay: "string",
    taxFailureReason: "string",
    totalCountDisplay: "number",
    totalQtyOrdered: "number",
    totalTax: "number",
    totalTaxDisplay: "string",
    trackId: "string",
    type: "string",
    typeDisplay: "string",
    unassignCart: "boolean",
    userLabel: "string",
    userRoles: "string",
    vmiLocationId: "string",
};

const orderProperties: { [key in keyof Partial<OrderModel>]: "boolean" | "string" | "number" | "date" | "custom" } = {
    billToCity: "string",
    billToPostalCode: "string",
    billToState: "string",
    properties: "custom",
    btAddress1: "string",
    btAddress2: "string",
    btAddress3: "string",
    btAddress4: "string",
    btCompanyName: "string",
    btCountry: "string",
    canAddAllToCart: "boolean",
    canAddToCart: "boolean",
    currencyCode: "string",
    currencySymbol: "string",
    customerNumber: "string",
    customerPO: "string",
    customerSequence: "string",
    customerVatNumber: "string",
    discountAmount: "number",
    discountAmountDisplay: "string",
    erpOrderNumber: "string",
    fulfillmentMethod: "string",
    handlingCharges: "number",
    handlingChargesDisplay: "string",
    id: "string",
    modifyDate: "date",
    notes: "string",
    orderDate: "date",
    orderDiscountAmount: "number",
    orderDiscountAmountDisplay: "string",
    orderGrandTotalDisplay: "string",
    orderSubTotal: "number",
    orderSubTotalDisplay: "string",
    orderTotal: "number",
    orderTotalDisplay: "string",
    otherCharges: "number",
    otherChargesDisplay: "string",
    productDiscountAmount: "number",
    productDiscountAmountDisplay: "string",
    productTotal: "number",
    productTotalDisplay: "string",
    requestedDeliveryDateDisplay: "date",
    salesperson: "string",
    shipCode: "string",
    shippingAndHandling: "number",
    shippingAndHandlingDisplay: "string",
    shippingCharges: "number",
    shippingChargesDisplay: "string",
    shipToCity: "string",
    shipToPostalCode: "string",
    shipToState: "string",
    shipViaDescription: "string",
    showTaxAndShipping: "boolean",
    stAddress1: "string",
    stAddress2: "string",
    stAddress3: "string",
    stAddress4: "string",
    status: "string",
    statusDisplay: "string",
    stCompanyName: "string",
    stCountry: "string",
    taxAmount: "number",
    taxAmountDisplay: "string",
    terms: "string",
    totalTaxDisplay: "string",
    vmiLocationId: "string",
    vmiLocationName: "string",
    webOrderNumber: "string",
};

export const allProperties = { ...cartProperties, ...orderProperties };

export const detailsPropertyFieldDefinitions: FieldDefinition[] = [
    {
        name: fields.customPathOverride,
        displayName: "Custom Path Override",
        editorTemplate: "CheckboxField",
        fieldType: "General",
        defaultValue: false,
        sortOrder: 0,
    },
    {
        name: fields.selectedProperty,
        displayName: "Property",
        editorTemplate: "DropDownField",
        fieldType: "General",
        options: getAllDistinctPropertiesAsOptions(),
        defaultValue: "customerPO",
        isVisible: item => !item.fields.customPathOverride,
        customFilter: (option, page) => {
            const pageName = page.type;
            if (pageName === "OrderDetailsPage") {
                return Object.keys(orderProperties).includes(String(option.value));
            }
            return Object.keys(cartProperties).includes(String(option.value));
        },
    },
    {
        name: fields.customProperty,
        displayName: "Custom Property Name",
        editorTemplate: "TextField",
        fieldType: "General",
        defaultValue: "",
        isVisible: item => {
            return item.fields.selectedProperty === "properties" && !item.fields.customPathOverride;
        },
        isEnabled: item => item?.fields.selectedProperty === "properties",
        isRequired: true,
    },
    {
        name: fields.customLabel,
        displayName: "Label",
        editorTemplate: "TextField",
        defaultValue: "",
        fieldType: "Translatable",
    },
    {
        name: fields.defaultValue,
        displayName: "Default Value",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "",
    },
    {
        name: fields.titleType,
        displayName: "Title Type",
        editorTemplate: "DropDownField",
        fieldType: "General",
        options: [
            { displayName: "h1", value: "h1" },
            { displayName: "h2", value: "h2" },
            { displayName: "h3", value: "h3" },
            { displayName: "h4", value: "h4" },
            { displayName: "h5", value: "h5" },
            { displayName: "h6", value: "h6" },
            { displayName: "p", value: "p" },
        ],
        defaultValue: "h2",
    },
    {
        name: fields.titleStyle,
        displayName: "Title Style",
        editorTemplate: "DropDownField",
        fieldType: "General",
        options: [
            { displayName: "h1", value: "h1" },
            { displayName: "h2", value: "h2" },
            { displayName: "h3", value: "h3" },
            { displayName: "h4", value: "h4" },
            { displayName: "h5", value: "h5" },
            { displayName: "h6", value: "h6" },
            { displayName: "p", value: "p" },
            { displayName: "body", value: "body" },
            { displayName: "legend", value: "legend" },
            { displayName: "label", value: "label" },
        ],
        defaultValue: "h6",
    },
    {
        name: fields.layout,
        displayName: "Layout",
        editorTemplate: "DropDownField",
        fieldType: "General",
        options: [
            { displayName: "Row", value: "row" },
            { displayName: "Column", value: "column" },
        ],
        defaultValue: "row",
    },
    {
        ...gapField.definition,
        defaultValue: ".5rem",
    },

    {
        name: fields.customCSS,
        displayName: "Custom CSS",
        editorTemplate: "CodeField",
        fieldType: "General",
        tab: advancedTab,
        defaultValue: defaultCustomCss,
        isVisible: (_, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
        validate: value => {
            if (value === undefined || value === null) {
                return "";
            }

            const result = cssLinter.parse(value, { silent: true });
            if (result?.stylesheet?.parsingErrors) {
                return result.stylesheet.parsingErrors.length <= 0
                    ? ""
                    : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
            }

            return "Unable to parse CSS";
        },
        options: {
            mode: "css",
            lint: true,
            autoRefresh: true,
        },
    },
    {
        name: fields.customPath,
        displayName: "Custom Path",
        editorTemplate: "TextField",
        fieldType: "General",
        defaultValue: "",
        isVisible: item => {
            return item.fields.customPathOverride;
        },
        isEnabled: item => item?.fields.customPathOverride,
        isRequired: true,
        sortOrder: 1,
    },
    {
        name: fields.customPathType,
        displayName: "Custom Path Type",
        editorTemplate: "DropDownField",
        fieldType: "General",
        options: [
            { displayName: "Boolean", value: "boolean" },
            { displayName: "String", value: "string" },
            { displayName: "Number", value: "number" },
            { displayName: "Date", value: "date" },
        ],
        defaultValue: "string",
        isVisible: item => {
            return item.fields.customPathOverride;
        },
        isEnabled: item => item?.fields.customPathOverride,
        isRequired: true,
        sortOrder: 2,
    },
];
